'use client';

import { useEffect, useState } from 'react';
import supabase from '@/lib/supabase';
import useAuthStore from '@/store/useAuthStore';
import { getCanonicalProfileRole } from '@/lib/profile-role';

type TeacherClassInfo = {
  teacherId: string;
  teacherName: string | null;
  inviteCodeUsed: string | null;
  joinedAt: string | null;
};

export default function useTeacherClassInfo(enabled = true) {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const [teacherClassInfo, setTeacherClassInfo] = useState<TeacherClassInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !user?.id || getCanonicalProfileRole(profile) !== 'student') {
      setTeacherClassInfo(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const loadTeacherClassInfo = async () => {
      try {
        const { data: relationship } = await supabase
          .from('teacher_student_relationships')
          .select('teacher_id, invite_code_used, joined_at')
          .eq('student_id', user.id)
          .order('joined_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!relationship) {
          if (!cancelled) {
            setTeacherClassInfo(null);
            setIsLoading(false);
          }
          return;
        }

        const { data: teacherProfile } = await supabase
          .from('profiles')
          .select('nickname, email, onboard_data')
          .eq('id', relationship.teacher_id)
          .maybeSingle();

        const teacherName =
          teacherProfile?.onboard_data?.profile?.name ||
          teacherProfile?.nickname ||
          teacherProfile?.email?.split('@')[0] ||
          null;

        if (!cancelled) {
          setTeacherClassInfo({
            teacherId: relationship.teacher_id,
            teacherName,
            inviteCodeUsed: relationship.invite_code_used || null,
            joinedAt: relationship.joined_at || null,
          });
          setIsLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to load teacher class info:', error);
          setTeacherClassInfo(null);
          setIsLoading(false);
        }
      }
    };

    void loadTeacherClassInfo();

    return () => {
      cancelled = true;
    };
  }, [enabled, profile, user?.id]);

  return {
    teacherClassInfo,
    hasTeacherClass: !!teacherClassInfo,
    isLoading,
  };
}
